import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  Avatar,
  AvatarGroup,
  Box,
  ButtonBase,
  Card,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import StatusBar from '@/designs-dev/device/StatusBar';
import TabBar, {
  DEFAULT_ITEMS,
  TAB_BAR_FLOAT_ROOM,
} from '@/designs-dev/device/TabBar';
import PushScreen from '@/designs-dev/device/PushScreen';
import { SheetSurface } from '@/designs-dev/device/BottomSheet';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import Navigation from '@/karehero-app/shell/Navigation';
import { APP_HEADER_H } from '@/karehero-app/shell/layout';
import NotificationsScreen from '@/karehero-app/notifications/NotificationsScreen';
import AppBar from '@/karehero-app/shell/AppBar';
import BackChevron from '@/karehero-app/shell/BackChevron';
import CircleSwitcher, {
  MenuRow,
  type CircleView,
} from '@/karehero-app/shell/CircleSwitcher';
import { CARED_FOR } from '@/karehero-app/shell/CircleSwitcher.content';
import { MEMBERS } from '@/karehero-app/onboarding/Onboarding.content';
import ClaimRow, {
  ClaimRowLarge,
} from '@/karehero-app/tell-us-once/intake/ClaimRow';
import AppTab from '@/karehero-app/shell/AppTab';
import HelpScreen, {
  type HelpList,
  type HelpRead,
} from '@/karehero-app/help/HelpScreen';
import HelpChat from '@/karehero-app/help/HelpChat';
import { AaAnswers, AaStages } from '@/karehero-app/care/AaClaim';
import RecheckAnswers from '@/karehero-app/care/RecheckAnswers';
import ResultBanner from '@/karehero-app/care/ResultBanner';
import { RECHECK, RECHECK_FOUND } from '@/karehero-app/care/Recheck.content';
import {
  AA_ANSWERS_PAGE,
  AA_PROGRESS,
  AA_FINISHED,
} from '@/karehero-app/care/AaClaim.content';
import HubShortlists, {
  type HubShortlistsInitial,
} from '@/karehero-app/care/HubShortlists';
import ClaimSheet from '@/karehero-app/tell-us-once/benefits/ClaimSheet';
import { type Claim } from '@/karehero-app/tell-us-once/benefits/ResultScreen';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';
import {
  ASSESS_ROWS,
  PROFILE_TABS,
  RECIPIENT,
  type ProfileTabId,
} from '@/karehero-app/tell-us-once/intake/content';
import { RESULT_1_0 } from '@/karehero-app/tell-us-once/benefits/calculator-1-0.content';
import { OTHER_BENEFITS } from '@/karehero-app/care/CareHub.fixtures';
import {
  CARE_HUB,
  CHECKED,
  HUB_MAX,
} from '@/karehero-app/care/CareHub.content';

/** A part of the hub a frame can open scrolled to. */
export type HubSection = 'forms' | 'arranging' | 'record';

/** What could lead on Home: the one thing to do next, above the areas. */
export type HubNudge =
  | 'check'
  | 'finances'
  | 'financesCall'
  | 'assessment'
  | 'assessmentCall'
  | 'poa'
  | 'benefit';

// Each area tile's pastel, when the hub tries them. Warm brand tints only: no
// greens, and no Lavender, which the palette retired.
const PASTELS: Record<string, string> = {
  money: 'custom.sunflower.200',
  arranging: 'custom.warmOrange.100',
  legal: 'custom.cherry.100',
  record: 'custom.coral.50',
  docs: 'custom.coral.50',
  calls: 'custom.cherry.50',
};

/** A card on the grid, and the page it opens. */
export type HubArea =
  'could' | 'forms' | 'money' | 'arranging' | 'legal' | 'record';

// Profile folds into Care, so the bar loses it.
const TABS = DEFAULT_ITEMS.filter((t) => t.id !== 'profile');
// And with Care as Home, Care goes too.
export const HOME_TABS = TABS.filter((t) => t.id !== 'care');

// Whether a form is started, as the row's chip: a status, never a count of
// questions, which says how far there is to go rather than how far they've
// come. Where there is nothing started, what the row offers instead.
const progress = (row: (typeof ASSESS_ROWS)[number]) =>
  row.action ?? (row.answered ? CARE_HUB.started : undefined);

const NOTIFICATIONS_TITLE = 'Notifications';

// SANDBOX. With a mascot, the people's heading is only the Talk to Keni
// button, and the card starts here: status bar 47, app bar, 16 of padding,
// then the header.
const MASCOT_HEADER_H = 56;
const CARD_TOP = 47 + APP_HEADER_H + 16 + MASCOT_HEADER_H;

// A pushed page's bar, drawn by the push so it can stay still and fade while
// the page's body slides in under it.
const pushedBar = (title: string | undefined, close: () => void) => (
  <AppBar
    title={title}
    size='small'
    leading={<BackChevron label='Back' onClick={close} />}
  />
);

const Section = ({
  title,
  id,
  children,
}: {
  /** A string draws the h4; anything else is drawn as it is. */
  title: ReactNode;
  id?: HubSection;
  children: ReactNode;
}) => (
  <Stack spacing={1.5} data-section={id}>
    {typeof title === 'string' ? (
      <Typography variant='h4'>{title}</Typography>
    ) : (
      title
    )}
    {children}
  </Stack>
);

/**
 * CONCEPT. Care as the hub for one person, in place of Profile's four toggles:
 * who you care for, what they could get, the forms, arranging care, and their
 * record last. One scroll, what to do before what we hold.
 *
 * Built from the pieces already in Loft: the switcher's people and circle, the
 * results' claim rows and drawer, Assess's forms, and the profile's own tabs,
 * each opened as a page.
 */
const CareHubScreen = ({
  initialPerson = RECIPIENT,
  initialSection,
  initialPage,
  initialCircle,
  openClaim,
  merged = false,
  grid = false,
  squares = false,
  tidy = false,
  subjects = false,
  nudge,
  summaryAbout = false,
  cardCircle = false,
  cardMenu = false,
  switchMotion = false,
  untitled = false,
  careForLine = false,
  pastel = false,
  pastelDots = false,
  iconColours = false,
  hideSummary = false,
  quietOthers = false,
  hasPoa = false,
  aaDone,
  aaFinished = false,
  aaNotEligible = false,
  unchecked = false,
  mascot,
  solo = false,
  emptyCircle = false,
  openPoa = false,
  initialArea,
  initialTab = 'hub',
  initialChat = false,
  initialHelpList,
  initialHelpRead,
  initialShortlistsOpen,
  initialRecheck = false,
  initialOthers = false,
  arrived = false,
  initialAaAnswers = false,
  initialRecheckEditing,
  rechecked: initialRechecked = false,
  initialShortlists,
}: {
  /** Whose hub it opens on. */
  initialPerson?: string;
  /** Harness-only: open scrolled to a section, for a flows page. */
  initialSection?: HubSection;
  /** Harness-only: open one of the record's pages. */
  initialPage?: ProfileTabId;
  /** Harness-only: open the circle's page, or adding someone. */
  initialCircle?: CircleView;
  /** Harness-only: open with this claim's drawer up. */
  openClaim?: string;
  /**
   * Concept 2: what they could get and the forms as one section, since they
   * are one job. Only the forms they have started show; the rest are behind
   * See every form.
   */
  merged?: boolean;
  /**
   * Concept 3: Care is Home. Under the people, the one thing to do next, then
   * a card per area, each opening its own page.
   */
  grid?: boolean;
  /**
   * The people as equal squares side by side, with the circle as its own row
   * under them: how the hub first drew them, kept as an option.
   */
  squares?: boolean;
  /**
   * The picked person's card, quieter: name and summary on one left edge, the
   * circle as its people's faces in the corner, and everyone else as chips.
   */
  tidy?: boolean;
  /**
   * The cards by subject rather than by format: Money, Arranging care, Legal,
   * About. No Forms card; each form is filed under what it is for, so nothing
   * can be in two places.
   */
  subjects?: boolean;
  /**
   * The one thing to do next, as a card above the areas, so Home says it
   * needs doing. The calculator before the check; the rest are the other
   * things that could lead.
   */
  nudge?: HubNudge;
  /**
   * With tidy: the person card opens About them, so About leaves the grid. The
   * circle comes off the name onto a row of its own, so inviting can't be read
   * as inviting the person.
   */
  summaryAbout?: boolean;
  /**
   * With tidy: the person card opens their care circle. About stays a tile.
   */
  cardCircle?: boolean;
  /**
   * With tidy: the person card opens a page of two, their care circle and
   * About them. Docs and calls come out of About into tiles of their own.
   */
  cardMenu?: boolean;
  /**
   * With tidy: switching by the chips plays a move. The tapped face flies up
   * into the card, the card's words come in behind it, and the page below
   * rises in, since all of it is about the person just picked.
   */
  switchMotion?: boolean;
  /** No "Who you care for" over the person card: the card is the heading. */
  untitled?: boolean;
  /** The summary opens "You care for …, who is", for a card with no heading. */
  careForLine?: boolean;
  /**
   * With grid: a Floral White page, the person card in Gardenia, and each area
   * tile in its own pastel, straight on the page rather than on a panel.
   */
  pastel?: boolean;
  /**
   * The same Floral White page and Gardenia card, but the tiles stay one
   * colour and only their icon discs take the pastels, so the nudge is still
   * the one strong colour on the page.
   */
  pastelDots?: boolean;
  /**
   * Only the icon discs in pastels, on the page as it is: Gardenia, the
   * darker panel, Floral White tiles.
   */
  iconColours?: boolean;
  /** With tidy: the person card is just their name, no summary under it. */
  hideSummary?: boolean;
  /** With tidy: everyone else as smaller, greyed cards under the picked one. */
  quietOthers?: boolean;
  /** They have told us there is a power of attorney in place. */
  hasPoa?: boolean;
  /**
   * How many of the Attendance Allowance claim's six stages are done. Set, the
   * claim's card and drawer show its stages rather than a count of questions.
   */
  aaDone?: number;
  /** Every stage is answered: its card says so and opens the result. */
  aaFinished?: boolean;
  /** With aaFinished: the answers say Margaret can't get it. */
  aaNotEligible?: boolean;
  /** Draw this person as not checked yet, whatever the fixtures say. */
  unchecked?: boolean;
  /**
   * SANDBOX. An illustration beside a bigger "Who you care for", sitting on
   * the person card's top edge.
   */
  mascot?: string;
  /** Only the one person: nobody else they care for yet. */
  solo?: boolean;
  /** Nobody in the care circle yet: the faces become an invite. */
  emptyCircle?: boolean;
  /** Harness-only: open with the power of attorney drawer up. */
  openPoa?: boolean;
  /** Harness-only: open one of the grid's pages. */
  initialArea?: HubArea;
  /** Harness-only: open on Help rather than the hub. */
  initialTab?: 'hub' | 'help';
  /** Harness-only: open with Help's chat up. */
  initialChat?: boolean;
  /** Harness-only: open Help onto every webinar or every article. */
  initialHelpList?: HelpList;
  /** Harness-only: open Help onto a webinar or article. */
  initialHelpRead?: HelpRead;
  /** Harness-only: open the claim's answers so far, from its drawer. */
  initialAaAnswers?: boolean;
  /**
   * Money arrived at from the end of the calculator: what it found, in a
   * banner at the top, the way a finished claim leads with its result.
   */
  arrived?: boolean;
  /** Harness-only: open the other benefits, from Money. */
  initialOthers?: boolean;
  /** Harness-only: open what they told the check, from Money. */
  initialRecheck?: boolean;
  /** Harness-only: with initialRecheck, open this answer's drawer. */
  initialRecheckEditing?: string;
  /** Harness-only: the check has been run again, with the hours changed. */
  rechecked?: boolean;
  /** Harness-only: open the shortlists over the hub. */
  initialShortlistsOpen?: boolean;
  /** Harness-only: open the shortlists onto a list, provider or booking. */
  initialShortlists?: HubShortlistsInitial;
}) => {
  const [tab, setTab] = useState(initialTab);
  const [chat, setChat] = useState(initialChat);
  const [who, setWho] = useState(initialPerson);
  const [area, setArea] = useState<HubArea | undefined>(initialArea);
  const [notes, setNotes] = useState(false);
  const [recheck, setRecheck] = useState(initialRecheck);
  const [others, setOthers] = useState(initialOthers);
  const [aaAnswers, setAaAnswers] = useState(initialAaAnswers);
  const [rechecked, setRechecked] = useState(initialRechecked);
  const [shortlists, setShortlists] = useState(initialShortlistsOpen);
  const [poaSheet, setPoaSheet] = useState(openPoa);
  const [page, setPage] = useState<ProfileTabId | undefined>(initialPage);
  const [circle, setCircle] = useState<CircleView | undefined>(initialCircle);
  const [claim, setClaim] = useState<Claim | undefined>(
    RESULT_1_0.claims.find((c) => c.name === openClaim),
  );
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!initialSection) return;
    // The screen's own scroll, not scrollIntoView, which moves the canvas too.
    const box = scroller.current;
    const el = box?.querySelector<HTMLElement>(
      `[data-section='${initialSection}']`,
    );
    if (box && el) box.scrollTop = el.offsetTop - box.offsetTop - 16;
  }, [initialSection]);

  // Switching with the quieter cards: each person's card slides from where it
  // was to where it lands, so the swap reads as the two trading places rather
  // than the page redrawing. Where each one was is noted before the switch,
  // then the move is played back from there once it has rendered.
  const was = useRef<Record<string, DOMRect>>({});
  const switchTo = (name: string) => {
    scroller.current
      ?.querySelectorAll<HTMLElement>('[data-person]')
      .forEach((el) => {
        was.current[el.dataset.person!] = el.getBoundingClientRect();
      });
    setWho(name);
  };
  useLayoutEffect(() => {
    const from = was.current;
    was.current = {};
    if (
      !Object.keys(from).length ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    scroller.current
      ?.querySelectorAll<HTMLElement>('[data-person]')
      .forEach((el) => {
        const before = from[el.dataset.person!];
        if (!before) return;
        const after = el.getBoundingClientRect();
        // The canvas can be zoomed, so the distance is in its pixels, not
        // the screen's.
        const zoom = el.offsetHeight ? after.height / el.offsetHeight : 1;
        el.animate(
          [
            {
              transform: `translateY(${(before.top - after.top) / zoom}px)`,
              opacity: 0.4,
            },
            { transform: 'none', opacity: 1 },
          ],
          { duration: 360, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
        );
      });
  }, [who]);

  // The chip's face, noted before a switch so the card's can fly from it.
  const below = useRef<HTMLDivElement>(null);
  const chipFace = useRef<DOMRect | undefined>(undefined);
  const pickChip = (name: string, chip: HTMLElement) => {
    chipFace.current = chip
      .querySelector('.MuiAvatar-root')
      ?.getBoundingClientRect();
    setWho(name);
  };
  useLayoutEffect(() => {
    const from = chipFace.current;
    chipFace.current = undefined;
    if (!from || window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      return;
    const box = scroller.current;
    const card = box?.querySelector<HTMLElement>('[data-picked]');
    const face = card?.querySelector<HTMLElement>('.MuiAvatar-root');
    if (!box || !card || !face) return;
    const ease = 'cubic-bezier(0.2, 0, 0, 1)';
    const to = face.getBoundingClientRect();
    // The canvas can be zoomed, so the distance is in its pixels.
    const zoom = face.offsetWidth ? to.width / face.offsetWidth : 1;
    face.animate(
      [
        {
          transformOrigin: 'top left',
          transform: `translate(${(from.left - to.left) / zoom}px, ${
            (from.top - to.top) / zoom
          }px) scale(${from.width / to.width})`,
        },
        { transformOrigin: 'top left', transform: 'none' },
      ],
      { duration: 460, easing: ease },
    );
    // The name and the summary, once the face is nearly there.
    card
      .querySelectorAll<HTMLElement>('.MuiTypography-root')
      .forEach((el) =>
        el.animate(
          [{ opacity: 0, transform: 'translateX(12px)' }, { opacity: 1 }],
          { duration: 300, delay: 140, easing: ease, fill: 'backwards' },
        ),
      );
    // The person just left, back in the chips.
    box.querySelectorAll<HTMLElement>('[data-chips] > *').forEach((el) =>
      el.animate([{ opacity: 0, transform: 'scale(0.9)' }, { opacity: 1 }], {
        duration: 260,
        delay: 200,
        easing: ease,
        fill: 'backwards',
      }),
    );
    // Then the nudge and the sections, one after another.
    [
      ...box.querySelectorAll<HTMLElement>('[data-rise]'),
      ...Array.from(below.current?.children ?? []).slice(1),
    ].forEach((el, i) =>
      el.animate(
        [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1 }],
        {
          duration: 360,
          delay: 220 + i * 60,
          easing: ease,
          fill: 'backwards',
        },
      ),
    );
  }, [who]);

  const checked = !unchecked && CHECKED.includes(who);
  // What checking again found that the first check didn't.
  const found = rechecked
    ? OTHER_BENEFITS.find((c) => c.name === RECHECK_FOUND)
    : undefined;
  // The claim's stages, where a frame says how far in it is.
  const isAa = (c: Claim) =>
    aaDone !== undefined && c.name === 'Attendance Allowance';
  const claims = checked ? RESULT_1_0.claims : [];
  // A form that is also a result shows once, as the result, with its progress
  // on it. One row per thing, wherever it is.
  const formOf = (name: string) => ASSESS_ROWS.find((r) => r.name === name);
  const forms = ASSESS_ROWS.filter(
    (r) => r.id !== 'calculator' && !claims.some((c) => c.name === r.name),
  ).sort((a, b) => Number(!!b.answered) - Number(!!a.answered));
  const calculator = ASSESS_ROWS.find((r) => r.id === 'calculator');
  const started = forms.filter((row) => row.answered);

  // Each area's rows, drawn once: inline on the scrolling hub, or on the
  // area's own page from the grid.
  const claimRow = (c: Claim, cherry = false, chip?: string) => {
    const form = formOf(c.name);
    const aa = isAa(c);
    return (
      <ClaimRow
        key={c.name}
        icon={c.icon}
        name={c.name}
        description={c.description}
        count={
          chip ??
          (aa
            ? aaFinished
              ? AA_FINISHED.chip
              : AA_PROGRESS.chip(aaDone ?? 0)
            : form
              ? progress(form)
              : undefined)
        }
        lead={c.lead}
        cherry={cherry}
        onClick={
          aa && aaFinished
            ? () => setAaAnswers(true)
            : c.about
              ? () => setClaim(c)
              : undefined
        }
      />
    );
  };
  const calculatorRow = calculator && (
    <ClaimRow
      icon={calculator.icon}
      name={calculator.name}
      description={calculator.description}
      lead
    />
  );
  const formRow = (row: (typeof ASSESS_ROWS)[number]) => (
    <ClaimRow
      key={row.id}
      icon={row.icon}
      name={row.name}
      description={row.description}
      count={progress(row)}
    />
  );
  const everyForm = (
    <Button variant='link' ariaLabel={CARE_HUB.everyForm}>
      {CARE_HUB.everyForm}
    </Button>
  );
  const arrangingRows = (
    <ClaimRow {...CARE_HUB.shortlists} onClick={() => setShortlists(true)} />
  );
  // The circle as a row of its own, off the person card.
  const circleRow = (
    <MenuRow
      icon='user-group'
      label={emptyCircle ? CARE_HUB.inviteLabel(who) : CARE_HUB.circle(who)}
      onClick={() => setCircle('manage')}
    />
  );
  // Each says what it is rather than how many are in it.
  const recordRows = CARE_HUB.recordRows.map((row) => (
    <ClaimRow
      key={row.id}
      icon={row.icon}
      name={PROFILE_TABS.find((t) => t.id === row.id)?.label ?? row.id}
      description={row.what}
      onClick={() => setPage(row.id)}
    />
  ));

  // The grid's cards, one per area.
  // A Floral White page, from either pastel take.
  const floral = pastel || pastelDots;
  // Pastel icon discs, on either page.
  const dots = pastelDots || iconColours;
  // What the person card opens, if anything.
  const cardOpens = summaryAbout || cardCircle || cardMenu;
  const areas: { id: HubArea | 'docs' | 'calls'; title: string }[] = subjects
    ? [
        { id: 'money', title: CARE_HUB.subjects.money },
        { id: 'arranging', title: CARE_HUB.arranging },
        { id: 'legal', title: CARE_HUB.subjects.legal },
        // The person card opens it instead.
        ...(summaryAbout || cardMenu
          ? []
          : [{ id: 'record' as const, title: CARE_HUB.record(who) }]),
        // Out of About, when the card holds About and the circle.
        ...(cardMenu
          ? (['docs', 'calls'] as const).map((id) => ({
              id,
              title: PROFILE_TABS.find((t) => t.id === id)?.label ?? id,
            }))
          : []),
      ]
    : [
        { id: 'could', title: CARE_HUB.couldGet(who) },
        { id: 'forms', title: CARE_HUB.forms },
        { id: 'arranging', title: CARE_HUB.arranging },
        { id: 'record', title: CARE_HUB.record(who) },
      ];
  // Financial status, said as what it does for them here.
  const finances = ASSESS_ROWS.find((r) => r.id === 'financial-status');
  // Once the check is done it is the next thing to do, so it takes the
  // check's look: the cherry fill.
  const financesRow = finances && (
    <ClaimRow
      icon={finances.icon}
      name={finances.name}
      description={CARE_HUB.moneyPage.howMuch(who)}
      count={progress(finances)}
      cherry={!checked}
      lead={checked}
      filled={checked ? 'cherry' : undefined}
    />
  );
  // What could lead on Home, each as the row it opens, in the check's cherry.
  // Worded as it is in its area unless CARE_HUB.lead says otherwise.
  const leadRow = (
    row: { icon: string; name: string; description: string },
    count?: string,
  ) => (
    <ClaimRow
      icon={row.icon}
      name={row.name}
      description={row.description}
      count={count}
      lead
      filled='cherry'
    />
  );
  const assessment = ASSESS_ROWS.find((r) => r.id === 'care-profile');
  // The rules say whether, not how much, so the results come in the order
  // they are worth: the first is the biggest.
  const topClaim = RESULT_1_0.claims[0];
  const topForm = formOf(topClaim.name);
  const leads: Record<HubNudge, ReactNode> = {
    // Worded as the prompt it is here. In Money, where it lives, it keeps the
    // tool's name.
    check:
      calculator &&
      leadRow({
        icon: calculator.icon,
        name: CARE_HUB.checkPrompt(who),
        description: CARE_HUB.checkLine(who),
      }),
    finances:
      finances &&
      leadRow(
        { ...finances, description: CARE_HUB.moneyPage.howMuch(who) },
        progress(finances),
      ),
    financesCall: leadRow({
      icon: 'phone',
      name: CARE_HUB.lead.call,
      description: CARE_HUB.lead.afterFinances(who),
    }),
    assessment:
      assessment &&
      leadRow(
        { ...assessment, description: CARE_HUB.assessment(who) },
        progress(assessment),
      ),
    assessmentCall: leadRow({
      icon: 'phone',
      name: CARE_HUB.lead.call,
      description: CARE_HUB.lead.afterAssessment(who),
    }),
    poa: leadRow({
      icon: CARE_HUB.areaIcons.legal,
      name: CARE_HUB.poa.name,
      description: CARE_HUB.poa.what(who),
    }),
    benefit: leadRow(topClaim, topForm && progress(topForm)),
  };
  // About keeps its name when the person card opens it rather than a tile.
  const areaTitle =
    area === 'record'
      ? cardMenu
        ? who
        : CARE_HUB.record(who)
      : areas.find((a) => a.id === area)?.title;
  const areaBody: Record<HubArea, ReactNode> = {
    // Before the check, what they could get is the calculator.
    could: checked ? claims.map((c) => claimRow(c)) : calculatorRow,
    forms: (
      <>
        {forms.map(formRow)}
        {everyForm}
      </>
    ),
    // What they could get and the claims for it, then the form about their
    // money. The calculator until they have checked.
    // After the check, what it found comes first, under its own heading, and
    // everything else after. Before it, the check itself leads, and the
    // benefits are a plain list nobody has said apply yet.
    // The check, then the finances straight under it: the check says what
    // they could get, the finances say how much. Never at the bottom: once
    // the check is done, the finances take its place at the top.
    money: (
      <Stack spacing={4}>
        {checked ? (
          <>
            {arrived && (
              <ResultBanner
                tone='lime'
                icon='check'
                name={CARE_HUB.arrived.name(
                  who,
                  claims.length + (found ? 1 : 0),
                )}
                description={CARE_HUB.arrived.description}
              />
            )}
            <Stack spacing={1.5}>
              {/* The check stays at the top, as a way back in: things change, and the
                  results are only as good as what they told us. */}
              {calculator && (
                <ClaimRow
                  icon={calculator.icon}
                  name={CARE_HUB.recheck.name}
                  description={CARE_HUB.recheck.description}
                  cherry
                  onClick={() => setRecheck(true)}
                />
              )}
              {/* Then how much is next. */}
              {financesRow}
            </Stack>
            <Section title={CARE_HUB.couldGet(who)}>
              {/* Checked again, what's new leads the results, marked. */}
              {found && claimRow(found, false, RECHECK.fresh)}
              {claims.map((c) => claimRow(c))}
            </Section>
            {/* Only what they could get on Money; everything else is a tap
                away, so the results aren't buried under what doesn't apply. */}
            <Box sx={{ alignSelf: 'flex-start', mt: -2 }}>
              <Button
                variant='link'
                isNoPadding
                ariaLabel={CARE_HUB.moneyPage.viewOthers}
                onPress={() => setOthers(true)}
              >
                {CARE_HUB.moneyPage.viewOthers}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Stack spacing={1.5}>
              {/* The Home prompt's look, so it reads as the one to do
                  first: the cherry fill, and a line saying why. */}
              {calculator && (
                <ClaimRow
                  icon={calculator.icon}
                  name={CARE_HUB.checkPrompt(who)}
                  description={CARE_HUB.checkLine(who)}
                  lead
                  filled='cherry'
                />
              )}
              {financesRow}
            </Stack>
            <Section title={CARE_HUB.moneyPage.all}>
              {[...RESULT_1_0.claims, ...OTHER_BENEFITS].map((c) =>
                claimRow({ ...c, lead: false }, true),
              )}
            </Section>
          </>
        )}
      </Stack>
    ),
    // The care assessment is what arranging care starts from.
    arranging: subjects ? (
      <>
        {assessment &&
          formRow({ ...assessment, description: CARE_HUB.assessment(who) })}
        {arrangingRows}
      </>
    ) : (
      arrangingRows
    ),
    // What it is on the row, more in the drawer. Once they have it, the row
    // says so and the drawer offers to keep the document with the rest.
    legal: (
      <>
        <ClaimRow
          icon='file-contract'
          name={CARE_HUB.poa.name}
          description={hasPoa ? CARE_HUB.poa.inPlace : CARE_HUB.poa.what(who)}
          count={hasPoa ? CARE_HUB.poa.inPlaceChip : undefined}
          onClick={() => setPoaSheet(true)}
        />
        <ClaimRow
          icon='phone'
          name={CARE_HUB.lead.call}
          description={CARE_HUB.legalCall(who)}
        />
        {/* The explainer as a prompt at the foot, not a step of its own:
          there whenever they want it. */}
        <Box sx={{ alignSelf: 'flex-start' }}>
          <Button
            variant='link'
            iconLeft={'circle-question' as never}
            isNoPadding
            ariaLabel={CARE_HUB.poa.sheetTitle}
            onPress={() => setPoaSheet(true)}
          >
            {CARE_HUB.poa.sheetTitle}
          </Button>
        </Box>
      </>
    ),
    // With no summary on Home, the circle lives in here with the rest of them.
    // First, above her record: the people around her.
    record: cardMenu ? (
      // The card's page: who is around them, and about them. Docs and calls
      // are tiles on Home instead.
      <>
        <ClaimRow
          icon='user-group'
          name={CARE_HUB.circleRow}
          description={CARE_HUB.circleWhat}
          onClick={() => setCircle('manage')}
        />
        {recordRows.filter((row) => row.key === 'profile')}
      </>
    ) : summaryAbout && hideSummary ? (
      <>
        <ClaimRow
          icon='user-group'
          name={CARE_HUB.circleRow}
          description={CARE_HUB.circleWhat}
          onClick={() => setCircle('manage')}
        />
        {recordRows}
      </>
    ) : (
      recordRows
    ),
  };

  const hubTab = grid ? 'home' : 'care';
  const tabs = grid ? HOME_TABS : TABS;

  // Help, the master's own screen, under the hub's tab bar. Talk to us opens
  // Intercom's messenger over it.
  if (tab === 'help') {
    return (
      <AppTab
        active='help'
        items={tabs}
        tone='neutral'
        onSelect={(id) => id === hubTab && setTab('hub')}
      >
        {/* No name in the bar, the same as the hub's own. */}
        <HelpScreen
          onTalk={() => setChat(true)}
          onNotifications={() => setNotes(true)}
          initialList={initialHelpList}
          initialRead={initialHelpRead}
        />
        {chat && <HelpChat onClose={() => setChat(false)} />}
        {notes && (
          <PushScreen
            onClose={() => setNotes(false)}
            bar={(close) => pushedBar(NOTIFICATIONS_TITLE, close)}
          >
            {(close) => <NotificationsScreen onBack={close} bare />}
          </PushScreen>
        )}
      </AppTab>
    );
  }

  return (
    // Card names at 18px and descriptions at 16px in the hub only.
    <ClaimRowLarge.Provider value>
      {/* Trying Floral White drawers in the hub. */}
      <SheetSurface.Provider value='paper'>
        {/* Isolated, so the sky can sit behind everything but the page itself. */}
        <ScreenRoot
          relative
          sx={{
            ...(mascot ? { isolation: 'isolate' } : {}),
            // Floral White under everything, so the Gardenia card and the
            // pastel tiles are the colour on the page.
            ...(floral ? { bgcolor: 'background.paper' } : {}),
          }}
        >
          {mascot && (
            // The sky, from the very top: behind the status bar and the app bar,
            // so the sunset starts in the corner. It stays put while the page
            // scrolls, the way a sky would.
            <Box
              component='img'
              src='/vector/sunset-sky.svg'
              alt=''
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: 260,
                zIndex: -1,
                pointerEvents: 'none',
              }}
            />
          )}
          {mascot && (
            <>
              {/* The hill, rising from behind where the card starts. */}
              <Box
                component='img'
                src='/vector/meadow-sunset.svg'
                alt=''
                sx={{
                  position: 'absolute',
                  top: CARD_TOP - 146,
                  left: 0,
                  width: '100%',
                  height: 188,
                  objectFit: 'cover',
                  // Mirrored, so the hill sits under the llama on the left and
                  // the button's side stays plain.
                  transform: 'scaleX(-1)',
                  zIndex: -1,
                  pointerEvents: 'none',
                }}
              />
              {/* The llama on it, up behind the app bar on the left, facing
              across to Talk to Keni. */}
              <Box
                component='img'
                src={mascot}
                alt=''
                sx={{
                  position: 'absolute',
                  top: CARD_TOP + 4 - 128,
                  left: 36,
                  height: 128,
                  zIndex: -1,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}
          <StatusBar />
          {/* No name in the bar: the people are on the page, so the bar has
          nothing to switch. */}
          <Navigation
            divider={!mascot}
            avatarInitials='OP'
            onNotifications={() => setNotes(true)}
          />

          <Box
            ref={scroller}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: 2,
              pt: 2,
              // Clear of the floating tab bar, which the page scrolls under.
              pb: `${TAB_BAR_FLOAT_ROOM}px`,
            }}
          >
            {/* Tighter on Home, which has two sections, not five. */}
            <Stack ref={below} spacing={grid ? 3 : 4}>
              <Section
                title={
                  mascot ? (
                    // Just the button: the llama and his hill sit behind the app
                    // bar, in the screen's own layer, so this takes little room.
                    <Box
                      sx={{
                        height: MASCOT_HEADER_H,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        pb: 1,
                      }}
                    >
                      <Button
                        variant='primary'
                        iconLeft={'comments' as never}
                        ariaLabel={CARE_HUB.talkToKeni}
                      >
                        {CARE_HUB.talkToKeni}
                      </Button>
                    </Box>
                  ) : untitled ? null : (
                    CARE_HUB.people
                  )
                }
              >
                {tidy ? (
                  <>
                    {CARED_FOR.filter((p) => p.name === who).map((person) => (
                      <Card
                        key={person.name}
                        data-person={quietOthers ? person.name : undefined}
                        data-picked
                        variant='option'
                        onClick={
                          cardCircle
                            ? () => setCircle('manage')
                            : summaryAbout || cardMenu
                              ? () => setArea('record')
                              : undefined
                        }
                        sx={{
                          borderColor: 'primary.main',
                          cursor: cardOpens ? 'pointer' : 'default',
                          ...(floral ? { bgcolor: 'custom.gardenia' } : {}),
                          // In front of the mascot, which tucks in behind it.
                          ...(mascot
                            ? { position: 'relative', zIndex: 1 }
                            : {}),
                        }}
                      >
                        {/* One row: who, then their circle. The summary runs
                        the card's full width under it. */}
                        <Stack spacing={1.5}>
                          <Stack
                            sx={{ alignItems: 'center' }}
                            direction='row'
                            spacing={1.5}
                          >
                            <Avatar
                              sx={{
                                width: 44,
                                height: 44,
                                bgcolor: 'custom.limeGreen.500',
                                color: 'primary.main',
                                fontSize: 16,
                              }}
                            >
                              {person.initials}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant='h4' noWrap>
                                {person.name}
                              </Typography>
                            </Box>
                            {/* The card opens somewhere, so it says so the way
                            the other things that open somewhere do. */}
                            {cardOpens && (
                              <Box sx={{ color: 'primary.main' }}>
                                <Icon
                                  icon={'chevron-right' as never}
                                  fontSize={14}
                                />
                              </Box>
                            )}
                            {/* The circle as the faces in it, as a pill with a
                            chevron so it reads as a control. */}
                            {!cardOpens && (
                              <ButtonBase
                                aria-label={
                                  emptyCircle
                                    ? CARE_HUB.inviteLabel(person.name)
                                    : CARE_HUB.circle(person.name)
                                }
                                onClick={() => setCircle('manage')}
                                // A pill with a chevron, the way the other
                                // things on this screen that open somewhere look,
                                // so the faces read as a control, not a picture.
                                sx={{
                                  flexShrink: 0,
                                  gap: 0.5,
                                  pl: emptyCircle ? 1.5 : 0.5,
                                  pr: emptyCircle ? 1.25 : 1,
                                  py: 0.5,
                                  borderRadius: 999,
                                  border: 1,
                                  borderColor: 'custom.neutral.200',
                                  bgcolor: 'background.paper',
                                  color: 'primary.main',
                                  '&:hover': {
                                    bgcolor: 'custom.neutral.100',
                                  },
                                }}
                              >
                                {emptyCircle ? (
                                  // Nobody to show yet, so the pill says what it
                                  // does instead.
                                  <>
                                    <Typography
                                      variant='body2'
                                      sx={{ color: 'text.primary', py: 0.5 }}
                                    >
                                      {CARE_HUB.invite}
                                    </Typography>
                                    <Icon
                                      icon={'plus' as never}
                                      fontSize={14}
                                    />
                                  </>
                                ) : (
                                  <AvatarGroup
                                    sx={{
                                      '& .MuiAvatar-root': {
                                        width: 28,
                                        height: 28,
                                        fontSize: 11,
                                      },
                                    }}
                                  >
                                    {[{ initials: 'OP' }, ...MEMBERS].map(
                                      (m) => (
                                        <Avatar
                                          key={m.initials}
                                          sx={{
                                            bgcolor: 'custom.neutral.400',
                                            color: 'custom.neutral.50',
                                          }}
                                        >
                                          {m.initials}
                                        </Avatar>
                                      ),
                                    )}
                                  </AvatarGroup>
                                )}
                                {/* One icon each way: the plus says it adds, the
                              chevron says the faces open somewhere. */}
                                {!emptyCircle && (
                                  <Icon
                                    icon={'chevron-right' as never}
                                    fontSize={12}
                                  />
                                )}
                              </ButtonBase>
                            )}
                          </Stack>
                          {!hideSummary && (
                            <Typography variant='body1'>
                              {CARE_HUB.summary[person.name]
                                ? careForLine
                                  ? CARE_HUB.youCareFor(
                                      person.name,
                                      CARE_HUB.summary[person.name],
                                    )
                                  : CARE_HUB.summary[person.name]
                                : CARE_HUB.noSummary(person.name)}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    ))}
                    {/* Off the card, so inviting reads as inviting to the
                    circle, not inviting the person. */}
                    {summaryAbout && !hideSummary && circleRow}
                    {/* Everyone else as the same card, smaller and greyed, so
                    they read as the same kind of thing but not the one Home
                    is about. Tapping one brings it up. */}
                    {quietOthers &&
                      !solo &&
                      CARED_FOR.filter((p) => p.name !== who).map((person) => (
                        <Card
                          key={person.name}
                          data-person={person.name}
                          variant='option'
                          onClick={() => switchTo(person.name)}
                          sx={{
                            py: 1,
                            // Inset, at the section's own gap from the card
                            // above.
                            mx: 1.5,
                            // Floral White, no edge: the plain card, smaller
                            // than the one Home is about.
                            bgcolor: 'background.paper',
                            borderColor: 'transparent',
                          }}
                        >
                          <Stack
                            direction='row'
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                // Neutral, so the Sunflower stays with the
                                // person Home is about.
                                bgcolor: 'custom.neutral.200',
                                color: 'primary.main',
                                fontSize: 12,
                              }}
                            >
                              {person.initials}
                            </Avatar>
                            <Typography
                              variant='body1'
                              sx={{ color: 'text.secondary' }}
                              noWrap
                            >
                              {person.name}
                            </Typography>
                          </Stack>
                        </Card>
                      ))}
                    {/* Everyone else as chips, so the picked card is the only
                    outline up here. Not with the quiet cards, which have them. */}
                    {!quietOthers && (
                      <Stack direction='row' spacing={1} data-chips>
                        {(solo
                          ? []
                          : CARED_FOR.filter((p) => p.name !== who)
                        ).map((person) => (
                          <Chip
                            key={person.name}
                            avatar={<Avatar>{person.initials}</Avatar>}
                            label={person.name}
                            onClick={(e) =>
                              switchMotion
                                ? pickChip(person.name, e.currentTarget)
                                : setWho(person.name)
                            }
                          />
                        ))}
                        <Chip
                          icon={<Icon icon={'plus' as never} fontSize={12} />}
                          label={CARE_HUB.add}
                          onClick={() => setCircle('add')}
                        />
                      </Stack>
                    )}
                  </>
                ) : squares ? (
                  <>
                    <Stack direction='row' spacing={1.5}>
                      {CARED_FOR.map((person) => {
                        const on = person.name === who;
                        return (
                          <Card
                            key={person.name}
                            variant='option'
                            onClick={() => setWho(person.name)}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              ...(on ? { borderColor: 'primary.main' } : {}),
                            }}
                          >
                            <Stack spacing={1}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: 'custom.limeGreen.500',
                                  color: 'primary.main',
                                  fontSize: 14,
                                }}
                              >
                                {person.initials}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant='h6' noWrap>
                                  {person.name}
                                </Typography>
                                <Typography
                                  sx={{ color: 'text.secondary' }}
                                  variant='body2'
                                  noWrap
                                >
                                  {person.relationship}
                                </Typography>
                              </Box>
                            </Stack>
                          </Card>
                        );
                      })}
                      <Card
                        variant='option'
                        onClick={() => setCircle('add')}
                        aria-label={CARE_HUB.add}
                        sx={{
                          width: 56,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main',
                        }}
                      >
                        <Icon icon={'plus' as never} fontSize={16} />
                      </Card>
                    </Stack>
                    <MenuRow
                      icon='user-group'
                      label={CARE_HUB.circle(who)}
                      onClick={() => setCircle('manage')}
                    />
                  </>
                ) : (
                  <>
                    {/* The one picked, across the width, with what we know about
                them. Everyone else in a smaller row under it. */}
                    {CARED_FOR.filter((p) => p.name === who).map((person) => (
                      <Card
                        key={person.name}
                        variant='option'
                        sx={{ borderColor: 'primary.main', cursor: 'default' }}
                      >
                        <Stack spacing={1.5}>
                          <Stack
                            sx={{ alignItems: 'center' }}
                            direction='row'
                            spacing={1.5}
                          >
                            <Avatar
                              sx={{
                                width: 44,
                                height: 44,
                                bgcolor: 'custom.limeGreen.500',
                                color: 'primary.main',
                                fontSize: 16,
                              }}
                            >
                              {person.initials}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant='h4' noWrap>
                                {person.name}
                              </Typography>
                              <Typography
                                sx={{ color: 'text.secondary' }}
                                variant='body2'
                                noWrap
                              >
                                {person.relationship}
                              </Typography>
                            </Box>
                          </Stack>
                          <Typography variant='body1'>
                            {CARE_HUB.summary[person.name] ??
                              CARE_HUB.noSummary(person.name)}
                          </Typography>
                          {/* On the card rather than a row of its own: the circle is
                      theirs, and a row under the people took a lot of the
                      screen for something opened now and then. */}
                          <Box sx={{ alignSelf: 'flex-start' }}>
                            <Button
                              variant='link'
                              iconLeft={'user-group' as never}
                              isNoPadding
                              ariaLabel={CARE_HUB.circle(person.name)}
                              onPress={() => setCircle('manage')}
                            >
                              {CARE_HUB.circle(person.name)}
                            </Button>
                          </Box>
                        </Stack>
                      </Card>
                    ))}
                    <Stack direction='row' spacing={1.5}>
                      {CARED_FOR.filter((p) => p.name !== who).map((person) => (
                        <Card
                          key={person.name}
                          variant='option'
                          onClick={() => setWho(person.name)}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          <Stack
                            sx={{ alignItems: 'center' }}
                            direction='row'
                            spacing={1}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: 'custom.limeGreen.500',
                                color: 'primary.main',
                                fontSize: 12,
                              }}
                            >
                              {person.initials}
                            </Avatar>
                            <Typography variant='h6' noWrap>
                              {person.name}
                            </Typography>
                          </Stack>
                        </Card>
                      ))}
                      <Card
                        variant='option'
                        onClick={() => setCircle('add')}
                        aria-label={CARE_HUB.add}
                        sx={{
                          width: 56,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main',
                        }}
                      >
                        <Icon icon={'plus' as never} fontSize={16} />
                      </Card>
                    </Stack>
                  </>
                )}
                {/* In the people's section, on their gap rather than a section's:
                it is about the person just picked, and it is the one thing to
                do before the cards. */}
                {grid && nudge && (nudge !== 'check' || !checked) && (
                  // A little more than the section's own gap, so it reads as the
                  // next thing rather than another chip.
                  <Box sx={{ pt: 1 }} data-rise>
                    {leads[nudge]}
                  </Box>
                )}
              </Section>

              {grid ? (
                // A panel a shade darker than the Gardenia page, the tiles Floral
                // White on it: the panel marks the zone, the cards stay plain.
                <Box
                  sx={{
                    // No panel on a Floral White page: the pastels mark the
                    // zone themselves.
                    p: floral ? 0 : 2,
                    // The same corner as the cards: the person card's 12px.
                    borderRadius: '12px',
                    bgcolor: (t) =>
                      floral
                        ? 'transparent'
                        : darken(t.palette.background.default, 0.04),
                  }}
                >
                  <Section title={CARE_HUB.areasTitle(who)}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1.5,
                      }}
                    >
                      {areas.map((a, i) => (
                        // The people squares' card: a round icon and the name. So the areas read as the same kind of thing.
                        <Card
                          key={a.id}
                          variant='option'
                          onClick={() =>
                            a.id === 'docs' || a.id === 'calls'
                              ? setPage(a.id)
                              : setArea(a.id)
                          }
                          sx={{
                            minWidth: 0,
                            // No edge: white on the panel's tint already
                            // separates, and the outline stays the person card's.
                            borderColor: 'transparent',
                            // Solid Floral White. See-through tiles picked up
                            // the darker panel and went grey. Or a pastel each.
                            bgcolor: pastel
                              ? PASTELS[a.id]
                              : pastelDots
                                ? 'custom.gardenia'
                                : 'background.paper',
                            // An odd one out spans the row rather than leaving a gap.
                            ...(areas.length % 2 && i === areas.length - 1
                              ? { gridColumn: '1 / -1' }
                              : {}),
                          }}
                        >
                          <Stack spacing={1}>
                            {/* Light cherry, so the areas read apart from the
                          people's lime. Marine Green was tried here and read
                          too blue and clinical. */}
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                // On a pastel, a Floral White disc instead.
                                // With pastel dots, the disc is the pastel and
                                // the icon Marine Green, leaving Cherry to the
                                // nudge.
                                bgcolor: dots
                                  ? PASTELS[a.id]
                                  : (t) =>
                                      pastel
                                        ? t.palette.background.paper
                                        : alpha(
                                            t.palette.custom.cherry[500],
                                            0.12,
                                          ),
                                color: dots
                                  ? 'primary.main'
                                  : 'custom.cherry.500',
                              }}
                            >
                              <Icon
                                icon={
                                  (a.id === 'docs' || a.id === 'calls'
                                    ? CARE_HUB.recordRows.find(
                                        (r) => r.id === a.id,
                                      )?.icon
                                    : CARE_HUB.areaIcons[a.id]) as never
                                }
                                fontSize={16}
                              />
                            </Avatar>
                            <Typography variant='title'>{a.title}</Typography>
                          </Stack>
                        </Card>
                      ))}
                    </Box>
                  </Section>
                </Box>
              ) : (
                <>
                  <Section
                    title={merged ? CARE_HUB.money : CARE_HUB.couldGet(who)}
                    id='forms'
                  >
                    {checked ? (
                      <>
                        {claims.slice(0, HUB_MAX).map((c) => {
                          const form = formOf(c.name);
                          return (
                            <ClaimRow
                              key={c.name}
                              icon={c.icon}
                              name={c.name}
                              description={c.description}
                              count={form ? progress(form) : undefined}
                              lead={c.lead}
                              onClick={c.about ? () => setClaim(c) : undefined}
                            />
                          );
                        })}
                        {!merged && claims.length > HUB_MAX && (
                          <Button
                            variant='link'
                            ariaLabel={CARE_HUB.seeAll(claims.length)}
                          >
                            {CARE_HUB.seeAll(claims.length)}
                          </Button>
                        )}
                      </>
                    ) : (
                      calculator && (
                        <ClaimRow
                          icon={calculator.icon}
                          name={calculator.name}
                          description={calculator.description}
                          lead
                        />
                      )
                    )}
                    {merged &&
                      forms
                        .filter((row) => row.answered)
                        .map((row) => (
                          <ClaimRow
                            key={row.id}
                            icon={row.icon}
                            name={row.name}
                            description={row.description}
                            count={progress(row)}
                          />
                        ))}
                    {merged && (
                      <Button variant='link' ariaLabel={CARE_HUB.everyForm}>
                        {CARE_HUB.everyForm}
                      </Button>
                    )}
                  </Section>

                  {!merged && (
                    <Section title={CARE_HUB.forms}>
                      {forms.map((row) => (
                        <ClaimRow
                          key={row.id}
                          icon={row.icon}
                          name={row.name}
                          description={row.description}
                          count={progress(row)}
                        />
                      ))}
                      <Button variant='link' ariaLabel={CARE_HUB.everyForm}>
                        {CARE_HUB.everyForm}
                      </Button>
                    </Section>
                  )}

                  <Section title={CARE_HUB.arranging} id='arranging'>
                    <ClaimRow {...CARE_HUB.shortlists} />
                  </Section>

                  <Section title={CARE_HUB.record(who)} id='record'>
                    {recordRows}
                  </Section>
                </>
              )}
            </Stack>
          </Box>

          <TabBar
            active={hubTab}
            items={tabs}
            tone='neutral'
            onPaper={floral}
            onSelect={(id) => id === 'help' && setTab('help')}
          />

          {/* The bell's page, the same one every tab opens. */}
          {notes && (
            <PushScreen
              onClose={() => setNotes(false)}
              bar={(close) => pushedBar(NOTIFICATIONS_TITLE, close)}
            >
              {(close) => <NotificationsScreen onBack={close} bare />}
            </PushScreen>
          )}

          {/* An area's page: the same rows as the scrolling hub, on their own. */}
          {area && (
            <PushScreen
              onClose={() => setArea(undefined)}
              bar={(close) => pushedBar(areaTitle, close)}
            >
              {() => (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
                  <Stack spacing={1.5}>{areaBody[area]}</Stack>
                </Box>
              )}
            </PushScreen>
          )}

          {/* The claim's answers so far, over Money. */}
          {aaAnswers && (
            <AaAnswers
              done={aaDone ?? 0}
              finished={aaFinished}
              eligible={!aaNotEligible}
              bar={pushedBar}
              onClose={() => setAaAnswers(false)}
            />
          )}

          {/* The benefits the check didn't point at, over Money. */}
          {others && (
            <PushScreen
              onClose={() => setOthers(false)}
              bar={(close) => pushedBar(CARE_HUB.moneyPage.others, close)}
            >
              {() => (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
                  <Stack spacing={1.5}>
                    {OTHER_BENEFITS.filter((c) => c !== found).map((c) =>
                      claimRow(c, true),
                    )}
                  </Stack>
                </Box>
              )}
            </PushScreen>
          )}

          {/* What they told the check, over Money. */}
          {recheck && (
            <RecheckAnswers
              title={calculator?.name ?? ''}
              bar={pushedBar}
              initialEditing={initialRecheckEditing}
              onClose={() => setRecheck(false)}
              onCheck={() => setRechecked(true)}
            />
          )}

          {/* Arranging care's shortlists, over its page. */}
          {shortlists && (
            <HubShortlists
              who={who}
              bar={pushedBar}
              initial={initialShortlists}
              onClose={() => setShortlists(false)}
            />
          )}

          {/* The record's pages are the profile's own tabs, one at a time. */}
          {page && (
            <PushScreen
              onClose={() => setPage(undefined)}
              bar={(close) =>
                pushedBar(PROFILE_TABS.find((t) => t.id === page)?.label, close)
              }
            >
              {(close) => (
                <CareProfileScreen initialTab={page} onBack={close} bare />
              )}
            </PushScreen>
          )}

          {circle && (
            <CircleSwitcher
              current={who}
              initialView={circle}
              onSwitch={setWho}
              onClose={() => setCircle(undefined)}
            />
          )}

          {poaSheet && (
            <ClaimSheet
              name={CARE_HUB.poa.sheetTitle}
              about={CARE_HUB.poa.about(who)}
              start={hasPoa ? undefined : CARE_HUB.poa.start}
              onStart={() => {
                setPoaSheet(false);
              }}
              onClose={() => setPoaSheet(false)}
            />
          )}

          {claim?.about && (
            <ClaimSheet
              name={claim.name}
              about={claim.about}
              start={
                isAa(claim)
                  ? aaDone
                    ? AA_PROGRESS.resume
                    : AA_PROGRESS.start
                  : RESULT_1_0.start
              }
              onStart={() => setClaim(undefined)}
              onClose={() => setClaim(undefined)}
            >
              {isAa(claim) && (
                <Stack spacing={1.5}>
                  <Typography variant='title'>{AA_PROGRESS.heading}</Typography>
                  <AaStages
                    done={aaDone ?? 0}
                    started={Boolean(aaDone)}
                    lines
                  />
                  {/* Once started, a way to what's been said so far. */}
                  {aaDone ? (
                    <Box sx={{ alignSelf: 'flex-start' }}>
                      <Button
                        variant='link'
                        isNoPadding
                        ariaLabel={AA_ANSWERS_PAGE.link}
                        onPress={() => {
                          setClaim(undefined);
                          setAaAnswers(true);
                        }}
                      >
                        {AA_ANSWERS_PAGE.link}
                      </Button>
                    </Box>
                  ) : null}
                </Stack>
              )}
            </ClaimSheet>
          )}
        </ScreenRoot>
      </SheetSurface.Provider>
    </ClaimRowLarge.Provider>
  );
};

export default CareHubScreen;
